// Preserve source chunks and indexes. Authoring is a metadata edit, not a mesh export.
const JSON_CHUNK = 0x4e4f534a;
const BIN_CHUNK = 0x004e4942;
const MAGIC = 0x46546c67;
const align4 = value => Math.ceil(value / 4) * 4;

export function readGlb(value) {
    const bytes = value instanceof Uint8Array ? new Uint8Array(value.buffer, value.byteOffset, value.byteLength) : new Uint8Array(value);
    if (bytes.byteLength < 20) throw new Error("This file is too small to be a GLB.");
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    if (view.getUint32(0, true) !== MAGIC || view.getUint32(4, true) !== 2) {
        throw new Error("Choose a glTF 2.0 binary model (.glb).");
    }
    if (view.getUint32(8, true) !== bytes.byteLength) throw new Error("The GLB file length is invalid.");
    const chunks = [];
    let offset = 12;
    while (offset < bytes.byteLength) {
        if (offset + 8 > bytes.byteLength) throw new Error("The GLB chunk header is incomplete.");
        const length = view.getUint32(offset, true);
        const type = view.getUint32(offset + 4, true);
        if (length % 4 || offset + 8 + length > bytes.byteLength) throw new Error("The GLB chunk length is invalid.");
        chunks.push({ type, bytes: bytes.slice(offset + 8, offset + 8 + length) });
        offset += 8 + length;
    }
    if (chunks[0]?.type !== JSON_CHUNK || chunks.filter(c => c.type === JSON_CHUNK).length !== 1
        || chunks.filter(c => c.type === BIN_CHUNK).length > 1) throw new Error("The GLB chunk layout is invalid.");
    if (chunks.some((c, i) => c.type === BIN_CHUNK && i !== 1)) throw new Error("The GLB binary buffer must follow its JSON.");
    const json = JSON.parse(new TextDecoder().decode(chunks[0].bytes).trimEnd());
    if (json.asset?.version !== "2.0") throw new Error("Only glTF 2.0 models can be authored.");
    const external = [...(json.buffers ?? []), ...(json.images ?? [])].find(item => item.uri && !item.uri.startsWith("data:"));
    if (external) throw new Error("This GLB references external resources. Embed its buffers and textures before importing it into Create.");
    return { json, chunks, binaryChunk: chunks.find(c => c.type === BIN_CHUNK)?.bytes ?? new Uint8Array() };
}

export function writeGlb(source, json, binary = source.binaryChunk) {
    const jsonBytes = new TextEncoder().encode(JSON.stringify(json));
    const jsonChunk = new Uint8Array(align4(jsonBytes.length)).fill(0x20);
    jsonChunk.set(jsonBytes);
    const chunks = [{ type: JSON_CHUNK, bytes: jsonChunk }];
    if (binary.length || source.chunks.some(c => c.type === BIN_CHUNK)) {
        const bytes = new Uint8Array(align4(binary.length));
        bytes.set(binary);
        chunks.push({ type: BIN_CHUNK, bytes });
    }
    chunks.push(...source.chunks.filter(c => c.type !== JSON_CHUNK && c.type !== BIN_CHUNK));
    const total = 12 + chunks.reduce((sum, chunk) => sum + 8 + chunk.bytes.length, 0);
    if (total > 0xffffffff) throw new Error("The exported model exceeds the GLB size limit.");
    const result = new Uint8Array(total);
    const view = new DataView(result.buffer);
    view.setUint32(0, MAGIC, true); view.setUint32(4, 2, true); view.setUint32(8, total, true);
    let offset = 12;
    for (const chunk of chunks) {
        view.setUint32(offset, chunk.bytes.length, true); view.setUint32(offset + 4, chunk.type, true);
        result.set(chunk.bytes, offset + 8); offset += 8 + chunk.bytes.length;
    }
    return result;
}

// New thumbnails become schema-v2 embedded images. Existing BIN bytes stay in place.
export function embedThumbnails(source, json) {
    const extension = json.extensions?.INNOWORKS_configurables;
    if (!extension) return source.binaryChunk;
    const pending = [];
    for (const container of extension.Containers ?? []) {
        for (const data of Object.values(container)) {
            if (!data || !Array.isArray(data.Options)) continue;
            for (const option of data.Options) {
                if (typeof option.ThumbnailBase64 === "string" && option.ThumbnailBase64.startsWith("data:image/png;base64,")) {
                    pending.push(option);
                }
            }
        }
    }
    if (!pending.length) return source.binaryChunk;
    if (json.buffers?.[0]?.uri) {
        // A valid GLB can keep all geometry in a data URI. Base64 thumbnails are
        // also part of the Unity schema; retain them without changing buffer 0.
        return source.binaryChunk;
    }
    if (!json.buffers?.length) json.buffers = [{ byteLength: 0 }];
    json.bufferViews = json.bufferViews ?? []; json.images = json.images ?? []; extension.EmbeddedImages = extension.EmbeddedImages ?? [];
    let length = align4(source.binaryChunk.length);
    const additions = [];
    const cache = new Map();
    for (const option of pending) {
        const uri = option.ThumbnailBase64;
        let index = cache.get(uri);
        if (index === undefined) {
            const bytes = Uint8Array.from(atob(uri.split(",")[1]), char => char.charCodeAt(0));
            const bufferView = json.bufferViews.length;
            json.bufferViews.push({ buffer: 0, byteOffset: length, byteLength: bytes.length });
            index = json.images.length;
            json.images.push({ bufferView, mimeType: "image/png", name: option.DisplayName + " thumbnail" });
            extension.EmbeddedImages.push({ ImageIndex: index, MimeType: "image/png", PayloadBufferViewIndex: bufferView, PayloadCompression: "None" });
            additions.push({ offset: length, bytes }); length = align4(length + bytes.length); cache.set(uri, index);
        }
        option.ThumbnailImageIndex = index;
        delete option.ThumbnailBase64;
    }
    const binary = new Uint8Array(length);
    binary.set(source.binaryChunk);
    additions.forEach(item => binary.set(item.bytes, item.offset));
    json.buffers[0].byteLength = length;
    return binary;
}
